import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Fab, IconButton, Link, Paper, TextField, Toolbar, Typography, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Link as RouterLink } from 'react-router-dom';

import { hasMagic } from '/packages/shared-components/utils/util';
import { readBedrockChatSse } from './bedrockChatStream';
import {
  BARE_URL_IN_TEXT,
  normalizeHttpUrl,
  prepareAssistantMarkdown,
  resolveMarkdownHref,
  toInternalRoute,
} from './chatMarkdownUtils';

/** Magic Form Mickey control size; chat launcher sits beside it, not under it. */
const MAGIC_FAB_SIZE_PX = 56;

/** Compact launcher; panel offset derived from size + edge inset. */
const CHAT_LAUNCHER_SIZE_PX = 48;
const CHAT_LAUNCHER_ICON_SIZE_PX = 22;
const CHAT_CLOSE_ICON_SIZE_PX = 18;
const CHAT_PANEL_WIDTH_PX = 376;
const CHAT_PANEL_MAX_HEIGHT_PX = 704;
const CHAT_PANEL_MIN_HEIGHT_PX = 250;
const CHAT_EDGE_INSET_PX = 20;
const CHAT_PANEL_BOTTOM_OFFSET_PX = CHAT_EDGE_INSET_PX + CHAT_LAUNCHER_SIZE_PX + CHAT_EDGE_INSET_PX;
const CHAT_PANEL_VIEWPORT_OFFSET_PX = CHAT_PANEL_BOTTOM_OFFSET_PX + CHAT_EDGE_INSET_PX;
const CHAT_INPUT_MAX_ROWS = 6;

type ChatMessage = { id: string; role: 'user' | 'assistant'; text: string };

type LinkifiedTextProps = {
  text: string;
  linkColor?: 'inherit' | 'primary';
};

function LinkifiedText({ text, linkColor = 'primary' }: LinkifiedTextProps) {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  const re = new RegExp(BARE_URL_IN_TEXT.source, BARE_URL_IN_TEXT.flags);
  let m: RegExpExecArray | null;
  let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) {
      nodes.push(<span key={`t-${i++}`}>{text.slice(last, m.index)}</span>);
    }
    const raw = m[0];
    const norm = normalizeHttpUrl(raw);
    if (norm) {
      nodes.push(
        <Link
          key={`l-${i++}`}
          href={norm.href}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          color={linkColor}
          underline={linkColor === 'inherit' ? 'always' : 'hover'}
          sx={{ wordBreak: 'break-all' }}
          onClick={(e) => e.stopPropagation()}
        >
          {norm.display}
        </Link>,
      );
      const afterLink = raw.slice(norm.display.length);
      if (afterLink) {
        nodes.push(<span key={`t-${i++}`}>{afterLink}</span>);
      }
    } else {
      nodes.push(<span key={`t-${i++}`}>{raw}</span>);
    }
    last = m.index + raw.length;
  }
  if (last < text.length) {
    nodes.push(<span key={`t-${i++}`}>{text.slice(last)}</span>);
  }
  return <>{nodes}</>;
}

type ChatMarkdownTypography = {
  fontFamily: string;
  bodySx: {
    fontFamily: string;
    fontSize: string | number;
    lineHeight: string | number;
    letterSpacing: string | number;
  };
  captionFontSize: string | number;
  captionLineHeight: string | number;
};

const getChatMarkdownTypography = (theme: Theme): ChatMarkdownTypography => {
  const fontFamily = theme.typography.fontFamily ?? 'Inspire, Helvetica Neue, Arial, sans-serif';
  const { fontSize, lineHeight, letterSpacing } = theme.typography.body2;
  const { fontSize: captionFontSize, lineHeight: captionLineHeight } = theme.typography.caption;

  return {
    fontFamily,
    bodySx: {
      fontFamily,
      fontSize: fontSize ?? '0.875rem',
      lineHeight: lineHeight ?? 1.429,
      letterSpacing: letterSpacing ?? 0,
    },
    captionFontSize: captionFontSize ?? '0.75rem',
    captionLineHeight: captionLineHeight ?? 1.2,
  };
};

const getChatMarkdownRootSx = (chatTypography: ChatMarkdownTypography) =>
  ({
    wordBreak: 'break-word',
    ...chatTypography.bodySx,
    '& > *:first-of-type': { mt: 0 },
    '& > *:last-child': { mb: 0 },
    '& p': { m: 0, mb: 0.5 },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      mt: 0.75,
      mb: 0.5,
      fontWeight: 600,
    },
    '& ul': { listStyleType: 'disc' },
    '& ol': { listStyleType: 'decimal' },
    '& ul, & ol': { my: 0.5, pl: 2.5 },
    '& li': { display: 'list-item', mb: 0.25 },
    '& li > p': { mb: 0 },
    '& blockquote': {
      my: 0.5,
      pl: 1.5,
      borderLeft: 3,
      borderColor: 'divider',
      color: 'text.secondary',
    },
    '& blockquote > *': {
      fontSize: 'inherit',
      lineHeight: 'inherit',
      letterSpacing: 'inherit',
    },
    '& pre': {
      my: 0.5,
      p: 1,
      overflow: 'auto',
      borderRadius: 1,
      bgcolor: 'action.hover',
      fontSize: chatTypography.captionFontSize,
      lineHeight: chatTypography.captionLineHeight,
    },
    '& :not(pre) > code': {
      px: 0.5,
      py: 0.125,
      borderRadius: 0.5,
      bgcolor: 'action.hover',
      fontSize: chatTypography.captionFontSize,
      lineHeight: chatTypography.captionLineHeight,
      fontFamily: 'monospace',
    },
    '& pre code': {
      bgcolor: 'transparent',
      p: 0,
      fontSize: 'inherit',
    },
    '& a': { wordBreak: 'break-all' },
    '& strong': { fontWeight: 600 },
  } as const);

const createChatMarkdownComponents = (chatTypography: ChatMarkdownTypography): Components => {
  const { bodySx } = chatTypography;
  const headingSx = { ...bodySx, fontWeight: 600 };

  return {
    p({ node: _node, ...rest }) {
      return (
        <Typography component="p" variant="body2" sx={{ ...bodySx, whiteSpace: 'pre-wrap', mb: 0.5, m: 0 }} {...rest} />
      );
    },
    h1({ node: _node, ...rest }) {
      return <Typography component="h1" variant="body2" sx={headingSx} {...rest} />;
    },
    h2({ node: _node, ...rest }) {
      return <Typography component="h2" variant="body2" sx={headingSx} {...rest} />;
    },
    h3({ node: _node, ...rest }) {
      return <Typography component="h3" variant="body2" sx={headingSx} {...rest} />;
    },
    h4({ node: _node, ...rest }) {
      return <Typography component="h4" variant="body2" sx={headingSx} {...rest} />;
    },
    h5({ node: _node, ...rest }) {
      return <Typography component="h5" variant="body2" sx={headingSx} {...rest} />;
    },
    h6({ node: _node, ...rest }) {
      return <Typography component="h6" variant="body2" sx={headingSx} {...rest} />;
    },
    ul({ node: _node, ...rest }) {
      return <Box component="ul" sx={{ my: 0.5, pl: 2.5, listStyleType: 'disc', ...bodySx }} {...rest} />;
    },
    ol({ node: _node, ...rest }) {
      return <Box component="ol" sx={{ my: 0.5, pl: 2.5, listStyleType: 'decimal', ...bodySx }} {...rest} />;
    },
    li({ node: _node, ...rest }) {
      return <Typography component="li" variant="body2" sx={{ ...bodySx, display: 'list-item', mb: 0.25 }} {...rest} />;
    },
    blockquote({ node: _node, ...rest }) {
      return (
        <Typography
          component="blockquote"
          variant="body2"
          color="text.secondary"
          sx={{ ...bodySx, my: 0.5, pl: 1.5, borderLeft: 3, borderColor: 'divider' }}
          {...rest}
        />
      );
    },
    a({ node: _node, href, children }) {
      if (!href) {
        return <span>{children}</span>;
      }
      const resolvedHref = resolveMarkdownHref(href);
      const internalTo = toInternalRoute(resolvedHref);
      if (internalTo) {
        return (
          <Link
            component={RouterLink}
            to={internalTo}
            variant="body2"
            color="primary"
            underline="hover"
            sx={bodySx}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </Link>
        );
      }
      return (
        <Link
          href={resolvedHref}
          target="_blank"
          rel="noopener noreferrer"
          variant="body2"
          color="primary"
          underline="hover"
          sx={bodySx}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </Link>
      );
    },
    code({ node: _node, ...rest }) {
      return <Box component="code" {...rest} />;
    },
    pre({ node: _node, ...rest }) {
      return <Box component="pre" {...rest} />;
    },
  };
};

type ChatMarkdownProps = {
  content: string;
};

export function ChatMarkdown({ content }: ChatMarkdownProps) {
  const theme = useTheme();
  const chatTypography = useMemo(() => getChatMarkdownTypography(theme), [theme]);
  const markdown = useMemo(() => prepareAssistantMarkdown(content), [content]);
  const markdownComponents = useMemo(() => createChatMarkdownComponents(chatTypography), [chatTypography]);

  return (
    <Box sx={getChatMarkdownRootSx(chatTypography)}>
      <ReactMarkdown components={markdownComponents}>{markdown}</ReactMarkdown>
    </Box>
  );
}

/** Opening tags: plain `<thinking>`, with attributes `<thinking …>`, or common aliases (`<think>`, `<think>`, `<reasoning>`). */
const THINKING_OPEN = /<\s*(?:redacted_thinking|thinking|think|reasoning)\b[^>]*>/i;
/** Closing tag must match the same family (any of the supported names). */
const THINKING_CLOSE = /<\s*\/\s*(?:redacted_thinking|thinking|think|reasoning)\s*>/i;

type AssistantMessageBodyProps = {
  text: string;
  fontFamily: string;
};

/** Replaces thinking/reasoning blocks (and in-progress open tag while streaming) with grey "Thinking…"; inner content is not shown. */
function AssistantMessageBody({ text, fontFamily }: AssistantMessageBodyProps) {
  const parts: React.ReactNode[] = [];
  let key = 0;
  let remaining = text;

  while (remaining.length > 0) {
    const openMatch = remaining.match(THINKING_OPEN);
    if (!openMatch || openMatch.index === undefined) {
      parts.push(
        <React.Fragment key={key++}>
          <ChatMarkdown content={remaining} />
        </React.Fragment>,
      );
      break;
    }

    const openIdx = openMatch.index;
    if (openIdx > 0) {
      parts.push(
        <React.Fragment key={key++}>
          <ChatMarkdown content={remaining.slice(0, openIdx)} />
        </React.Fragment>,
      );
    }

    const afterOpen = remaining.slice(openIdx + openMatch[0].length);
    const closeMatch = afterOpen.match(THINKING_CLOSE);

    parts.push(
      <Typography
        key={key++}
        variant="body2"
        component="span"
        sx={{ display: 'block', color: 'text.secondary', fontFamily }}
      >
        Thinking...
      </Typography>,
    );

    if (!closeMatch || closeMatch.index === undefined) {
      break;
    }

    remaining = afterOpen.slice(closeMatch.index + closeMatch[0].length).replace(/^\n+/, '');
  }

  return <Box sx={{ wordBreak: 'break-word' }}>{parts}</Box>;
}

const CHAT_URL = '/chatbot/chat';

export interface FloatingChatAssistantProps {
  isUserLoading: boolean;
  isUserAuthenticated: boolean;
}

export const FloatingChatAssistant = ({ isUserLoading, isUserAuthenticated }: FloatingChatAssistantProps) => {
  const theme = useTheme();
  const chatFontFamily = theme.typography.fontFamily ?? 'Inspire, Helvetica Neue, Arial, sans-serif';
  const chatBodyTypography = theme.typography.body2;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      text: '',
    },
  ]);
  const [sending, setSending] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const sendButtonRef = useRef<HTMLButtonElement>(null);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  /** Clicking Send parks focus on the button, which then disables and drops focus to the body. */
  const refocusInput = useCallback(() => {
    const active = document.activeElement;
    if (!active || active === document.body || active === sendButtonRef.current) {
      inputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (open) scrollToBottom();
  }, [messages, open, scrollToBottom]);

  useEffect(() => {
    if (!open) return;
    const frameId = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frameId);
  }, [open]);

  /** After a response completes the user should be able to keep typing without clicking the input again. */
  useEffect(() => {
    if (!open || sending) return;
    const frameId = requestAnimationFrame(refocusInput);
    return () => cancelAnimationFrame(frameId);
  }, [open, sending, refocusInput]);

  if (isUserLoading || !isUserAuthenticated) {
    return null;
  }

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;

    const userMsg: ChatMessage = { id: `u-${Date.now()}`, role: 'user', text };
    const assistantId = `a-${Date.now()}`;
    setMessages((m) => [...m, userMsg, { id: assistantId, role: 'assistant', text: '' }]);
    setInput('');
    setSending(true);

    const setAssistantText = (updater: (prev: string) => string) => {
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, text: updater(msg.text) } : msg)));
    };

    try {
      const res = await fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: text }),
      });

      if (!res.ok) {
        const errBody = await res.text();
        setAssistantText(() =>
          errBody ? `Request failed (${res.status}): ${errBody}` : `Request failed (${res.status})`,
        );
        return;
      }

      await readBedrockChatSse(res, (ev) => {
        if (ev.type === 'chunk' && ev.text) {
          setAssistantText((prev) => prev + ev.text);
        }
        if (ev.type === 'error') {
          setAssistantText(() => ev.error);
        }
      });

      setMessages((m) => {
        const row = m.find((x) => x.id === assistantId);
        if (row && row.text === '') {
          return m.map((msg) => (msg.id === assistantId ? { ...msg, text: '(No text in response.)' } : msg));
        }
        return m;
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Network error';
      setAssistantText(() => msg);
    } finally {
      setSending(false);
    }
  };

  /** Above most app chrome; FAB stays above the panel for tap target. */
  const zPanel = 1300;
  const zFab = 1301;

  // Align with `MagicForm` in UserProvider: MAGIC build + `hasMagic()` localStorage.
  const isMagicFormVisible = process.env.MAGIC === 'true' && hasMagic();
  const rightInset = isMagicFormVisible
    ? `calc(${CHAT_EDGE_INSET_PX}px + ${MAGIC_FAB_SIZE_PX}px + ${theme.spacing(1)})`
    : `${CHAT_EDGE_INSET_PX}px`;

  return (
    <>
      {open && (
        <Paper
          elevation={8}
          role="dialog"
          aria-label="Assistant chat"
          sx={{
            position: 'fixed',
            right: rightInset,
            bottom: `${CHAT_PANEL_BOTTOM_OFFSET_PX}px`,
            width: CHAT_PANEL_WIDTH_PX,
            maxWidth: `calc(100vw - ${CHAT_EDGE_INSET_PX * 2}px)`,
            height: `calc(100vh - ${CHAT_PANEL_VIEWPORT_OFFSET_PX}px)`,
            minHeight: CHAT_PANEL_MIN_HEIGHT_PX,
            maxHeight: CHAT_PANEL_MAX_HEIGHT_PX,
            zIndex: zPanel,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: 2,
            boxShadow: '0 5px 40px rgba(0, 0, 0, 0.16)',
            fontFamily: chatFontFamily,
            fontSize: chatBodyTypography.fontSize,
            lineHeight: chatBodyTypography.lineHeight,
            letterSpacing: chatBodyTypography.letterSpacing,
          }}
        >
          <Toolbar
            variant="dense"
            sx={{
              minHeight: 48,
              borderBottom: 1,
              borderColor: 'divider',
              justifyContent: 'space-between',
              px: 1.5,
            }}
          >
            <Typography variant="subtitle2" component="span" sx={{ fontFamily: chatFontFamily }}>
              Assistant
            </Typography>
            <IconButton size="small" aria-label="Close chat" onClick={() => setOpen(false)} edge="end" sx={{ p: 0.5 }}>
              <CloseIcon sx={{ fontSize: CHAT_CLOSE_ICON_SIZE_PX }} />
            </IconButton>
          </Toolbar>
          <Box
            ref={listRef}
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 1.5,
              py: 1,
              bgcolor: 'action.hover',
            }}
          >
            {messages.map((msg) => (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  mb: 1,
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    maxWidth: '85%',
                    bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                    color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  }}
                >
                  {msg.role === 'assistant' ? (
                    msg.id === 'welcome' ? (
                      <Box sx={{ fontFamily: chatFontFamily }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          component="div"
                          sx={{ mb: 0.5, fontFamily: 'inherit' }}
                        >
                          Welcome to the Partner Portal. <br />
                          As your Assistant, how may I help you?
                        </Typography>
                        <Typography variant="body2" component="div" sx={{ fontFamily: 'inherit' }}>
                          Please note: If you refresh the current browser page, your search results for this session
                          will be lost.
                        </Typography>
                      </Box>
                    ) : (
                      <AssistantMessageBody text={msg.text} fontFamily={chatFontFamily} />
                    )
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: chatFontFamily }}
                    >
                      <LinkifiedText text={msg.text} linkColor="inherit" />
                    </Typography>
                  )}
                </Paper>
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 0.5,
              p: 1,
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={1}
              maxRows={CHAT_INPUT_MAX_ROWS}
              placeholder={sending ? 'Waiting for response…' : 'Message…'}
              value={input}
              inputRef={inputRef}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void send();
                }
              }}
              inputProps={{ 'aria-label': 'Message input' }}
              sx={{
                '& .MuiInputBase-root': {
                  fontFamily: chatFontFamily,
                  fontSize: chatBodyTypography.fontSize,
                  lineHeight: chatBodyTypography.lineHeight,
                  letterSpacing: chatBodyTypography.letterSpacing,
                  alignItems: 'flex-end',
                },
                '& .MuiInputBase-inputMultiline': {
                  overflowY: 'auto',
                },
              }}
            />
            <IconButton
              color="primary"
              aria-label="Send message"
              onClick={() => void send()}
              edge="end"
              ref={sendButtonRef}
              disabled={sending || input.trim().length === 0}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}
      <Fab
        color="primary"
        aria-label={open ? 'Close assistant' : 'Open assistant'}
        onClick={() => setOpen((v) => !v)}
        sx={{
          position: 'fixed',
          right: rightInset,
          bottom: `${CHAT_EDGE_INSET_PX}px`,
          zIndex: zFab,
          width: CHAT_LAUNCHER_SIZE_PX,
          height: CHAT_LAUNCHER_SIZE_PX,
          minHeight: CHAT_LAUNCHER_SIZE_PX,
          boxShadow: '0 1px 6px rgba(0, 0, 0, 0.06), 0 2px 32px rgba(0, 0, 0, 0.16)',
        }}
      >
        {open ? (
          <CloseIcon sx={{ fontSize: CHAT_LAUNCHER_ICON_SIZE_PX }} />
        ) : (
          <ChatBubbleOutlineIcon sx={{ fontSize: CHAT_LAUNCHER_ICON_SIZE_PX }} />
        )}
      </Fab>
    </>
  );
};

export type ChatAssistantUserState = {
  currentUser?: object | null;
  loadingUser: boolean;
  permissions?: { isPartner?: boolean; isDisney?: boolean };
};

export type UseChatAssistantUser = () => ChatAssistantUserState;

export interface ConnectedFloatingChatAssistantProps {
  useUser: UseChatAssistantUser;
}

export const ConnectedFloatingChatAssistant = ({ useUser }: ConnectedFloatingChatAssistantProps) => {
  const { currentUser, loadingUser } = useUser();

  return <FloatingChatAssistant isUserLoading={loadingUser} isUserAuthenticated={!!currentUser} />;
};
